import type { YMarketCurrencyValue, YMarketItem, YMarketPrices } from '@/lib/types'

interface FinancialFact {
  label: string
  value: YMarketCurrencyValue
}

function displayMoney(money: YMarketCurrencyValue): string | null {
  if (money.value == null) return null
  const amount = money.value.toLocaleString('ru-RU')
  const currency = money.currencyId?.toUpperCase()
  if (currency === 'RUR' || currency === 'RUB') return `${amount} ₽`
  return currency ? `${amount} ${currency}` : amount
}

function factsFor(item: YMarketItem, fallbackPrices?: YMarketPrices): FinancialFact[] {
  const prices = item.prices

  const buyerPrice = prices?.buyerPricePerItem ?? prices?.buyerPrice
  const candidates: Array<[string, YMarketCurrencyValue | undefined]> = [
    ['Цена покупки', buyerPrice],
    ['Оплата', prices?.payment ?? fallbackPrices?.payment],
    ['Субсидия', prices?.subsidy ?? fallbackPrices?.subsidy],
    ['Кэшбэк', prices?.cashback ?? fallbackPrices?.cashback],
  ]
  return candidates.flatMap(([label, value]) => {
    if (!value || displayMoney(value) === null) return []
    return [{ label, value }]
  })
}

function uinsFor(item: YMarketItem): string[] {
  return (item.instances ?? [])
    .map((instance) => instance.uin)
    .filter((uin): uin is string => Boolean(uin))
}

export function OrderItemFinancials({ item, fallbackPrices, className = '' }: {
  item: YMarketItem
  fallbackPrices?: YMarketPrices
  className?: string
}) {
  const facts = factsFor(item, fallbackPrices)
  const uins = uinsFor(item)

  return (
    <div
      data-testid="order-item-financials"
      className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] leading-snug text-ink-muted ${className}`}
    >
      {facts.map(({ label, value }, index) => (
        <span key={label} className="whitespace-nowrap">
          {index > 0 ? <span aria-hidden="true" className="mr-2 text-ink-faint">·</span> : null}
          {label}: <span className="font-medium tab-nums text-ink-2">{displayMoney(value)}</span>
        </span>
      ))}
      {uins.map((uin, index) => (
        <span key={`uin-${uin}`} className="whitespace-nowrap">
          <span aria-hidden="true" className="mr-2 text-ink-faint">·</span>
          УИН: <span className="font-medium tab-nums text-ink-2">{uin}</span>
        </span>
      ))}
      {facts.length === 0 && uins.length === 0 ? (
        <span className="text-ink-faint">Цена недоступна</span>
      ) : null}
    </div>
  )
}
